import axios from 'axios';
import { ReportType } from '../pages/Reports/Reports';
import { API_BASE_URL } from '../utils/apiConfig';

export interface AvailableField {
  id: string;
  label: string;
}

export interface RelatedTable {
  table: string;
  label: string;
  fields: AvailableField[];
  relation_type: string;
  description: string;
}

export interface AvailableFieldsResponse {
  main_type: string;
  main_label: string;
  main_fields: AvailableField[];
  related_tables: RelatedTable[];
}

export interface ValidationResponse {
  valid: boolean;
  error: string | null;
}

// --- Saved Report Configs ---

export interface SavedReportConfig {
  id: number;
  name: string;
  report_type: ReportType;
  report_type_display: string;
  selected_fields: string[];
  filters: Record<string, any>;
  is_default: boolean;
  fields_count: number;
  created_at: string;
  updated_at: string;
}

export interface SavedReportConfigListItem {
  id: number;
  name: string;
  report_type: ReportType;
  report_type_display: string;
  fields_count: number;
  is_default: boolean;
  updated_at: string;
}

export interface SaveReportConfigPayload {
  name: string;
  report_type: ReportType;
  selected_fields: string[];
  filters?: Record<string, any>;
  is_default?: boolean;
}

/**
 * Waliduje, czy wybrane pola mogą być połączone w raporcie
 */
export const validateReportFields = async (
  reportType: ReportType,
  fields: string[]
): Promise<ValidationResponse> => {
  const url = `${API_BASE_URL}/reports/validate-fields/`;
  const requestData = {
    report_type: reportType,
    fields: fields,
  };
  
  console.log('[ReportsAPI] validateReportFields - URL:', url);
  console.log('[ReportsAPI] validateReportFields - Request data:', requestData);
  
  try {
    const response = await axios.post<ValidationResponse>(url, requestData);
    console.log('[ReportsAPI] validateReportFields - Success:', response.status, response.data);
    return response.data;
  } catch (error: any) {
    console.error('[ReportsAPI] validateReportFields - Error:', error);
    console.error('[ReportsAPI] validateReportFields - Error response:', error.response);
    
    // Jeśli backend zwraca błąd, spróbuj go odczytać
    if (error.response?.data) {
      return {
        valid: false,
        error: error.response.data.error || error.response.data.detail || 'Błąd podczas walidacji pól'
      };
    }
    
    return {
      valid: false,
      error: 'Nie udało się zwalidować pól raportu'
    };
  }
};

/**
 * Pobiera dostępne pola dla danego typu raportu
 */
export const getAvailableFields = async (
  mainType: ReportType
): Promise<AvailableFieldsResponse> => {
  const url = `${API_BASE_URL}/reports/available_fields/`;
  console.log('[ReportsAPI] getAvailableFields - URL:', url);
  console.log('[ReportsAPI] getAvailableFields - params:', { main_type: mainType });
  
  try {
    const response = await axios.get<AvailableFieldsResponse>(url, {
      params: { main_type: mainType },
    });
    console.log('[ReportsAPI] getAvailableFields - Success:', response.status, response.data);
    return response.data;
  } catch (error: any) {
    console.error('[ReportsAPI] getAvailableFields - Error:', error);
    console.error('[ReportsAPI] getAvailableFields - Error response:', error.response);
    throw error;
  }
};

/**
 * Generuje raport CSV i pobiera go jako plik
 * @param reportType Typ raportu do wygenerowania
 * @param fields Lista ID pól do eksportu (może być w formacie "table.field" lub "field")
 * @param filters Opcjonalne filtry (data, status, etc.)
 */
export const generateReport = async (
  reportType: ReportType,
  fields: string[],
  filters?: Record<string, any>
): Promise<void> => {
  const url = `${API_BASE_URL}/reports/generate_report/`;
  const requestData = {
    report_type: reportType,
    fields: fields,
    filters: filters || {},
  };
  
  console.log('[ReportsAPI] generateReport - URL:', url);
  console.log('[ReportsAPI] generateReport - Request data:', requestData);
  console.log('[ReportsAPI] generateReport - API_BASE_URL:', API_BASE_URL);
  
  try {
    const response = await axios.post(
      url,
      requestData,
      {
        responseType: 'blob', // Ważne: oczekujemy pliku binarnego
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    console.log('[ReportsAPI] generateReport - Success:', response.status, response.headers);
    console.log('[ReportsAPI] generateReport - Response data type:', typeof response.data, response.data instanceof Blob);

    // Utwórz link do pobrania pliku
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = blobUrl;
    
    // Nazwa pliku z timestampem
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    link.setAttribute('download', `${reportType}_${timestamp}.csv`);
    
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error: any) {
    console.error('[ReportsAPI] generateReport - Error:', error);
    console.error('[ReportsAPI] generateReport - Error response:', error.response);
    console.error('[ReportsAPI] generateReport - Error status:', error.response?.status);
    console.error('[ReportsAPI] generateReport - Error data:', error.response?.data);
    console.error('[ReportsAPI] generateReport - Error headers:', error.response?.headers);
    
    // Jeśli backend zwraca błąd jako JSON w blob, spróbuj go odczytać
    if (error.response?.data instanceof Blob) {
      console.log('[ReportsAPI] generateReport - Error is Blob, trying to read...');
      const text = await error.response.data.text();
      console.log('[ReportsAPI] generateReport - Error blob text:', text);
      try {
        const errorData = JSON.parse(text);
        console.error('[ReportsAPI] generateReport - Parsed error data:', errorData);
        throw new Error(errorData.detail || errorData.error || 'Błąd podczas generowania raportu');
      } catch {
        throw new Error('Nie udało się wygenerować raportu');
      }
    }
    throw error;
  }
};

// --- Saved Report Configs API ---

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Pobiera listę zapisanych konfiguracji raportów dla zalogowanego użytkownika.
 * Opcjonalnie filtruje po typie raportu.
 */
export const getSavedReportConfigs = async (
  reportType?: ReportType
): Promise<SavedReportConfigListItem[]> => {
  const url = `${API_BASE_URL}/report-configs/`;
  const params: Record<string, string> = {};
  if (reportType) {
    params.report_type = reportType;
  }
  // Fetch all configs (up to max page size) to avoid pagination complexity
  (params as any).page_size = '100';

  const response = await axios.get<PaginatedResponse<SavedReportConfigListItem>>(url, { params });
  return response.data.results;
};

/**
 * Pobiera szczegóły zapisanej konfiguracji raportu.
 */
export const getSavedReportConfig = async (
  id: number
): Promise<SavedReportConfig> => {
  const url = `${API_BASE_URL}/report-configs/${id}/`;
  const response = await axios.get<SavedReportConfig>(url);
  return response.data;
};

/**
 * Zapisuje nową konfigurację raportu.
 */
export const createSavedReportConfig = async (
  payload: SaveReportConfigPayload
): Promise<SavedReportConfig> => {
  const url = `${API_BASE_URL}/report-configs/`;
  const response = await axios.post<SavedReportConfig>(url, payload);
  return response.data;
};

/**
 * Aktualizuje istniejącą konfigurację raportu.
 */
export const updateSavedReportConfig = async (
  id: number,
  payload: Partial<SaveReportConfigPayload>
): Promise<SavedReportConfig> => {
  const url = `${API_BASE_URL}/report-configs/${id}/`;
  const response = await axios.patch<SavedReportConfig>(url, payload);
  return response.data;
};

/**
 * Usuwa zapisaną konfigurację raportu.
 */
export const deleteSavedReportConfig = async (
  id: number
): Promise<void> => {
  const url = `${API_BASE_URL}/report-configs/${id}/`;
  await axios.delete(url);
};
